import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { KeyRound, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export const WorkshopAccountsDialog = ({ workshop, el }) => {
    const [open, setOpen] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => api.get(`/leasing/workshops/${workshop.id}/accounts`).then((r) => setAccounts(r.data)).catch(() => setAccounts([])), [workshop.id]);
    useEffect(() => { if (open) load(); }, [open, load]);

    const create = async () => {
        setSaving(true);
        try {
            await api.post(`/leasing/workshops/${workshop.id}/accounts`, form);
            toast.success(el ? 'Ο λογαριασμός δημιουργήθηκε' : 'Account created');
            setForm({ name: '', email: '', password: '' });
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (a) => {
        if (!window.confirm(el ? `Διαγραφή λογαριασμού ${a.email};` : `Delete ${a.email}?`)) return;
        await api.delete(`/leasing/workshops/${workshop.id}/accounts/${a.id}`).catch(() => {});
        load();
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="text-slate-600" title={el ? 'Λογαριασμοί σύνδεσης' : 'Login accounts'} data-testid={`workshop-accounts-${workshop.id}`}><KeyRound className="w-4 h-4" /></Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white" data-testid="workshop-accounts-dialog">
                    <DialogHeader><DialogTitle>{el ? 'Λογαριασμοί σύνδεσης' : 'Login accounts'} - {workshop.name}</DialogTitle></DialogHeader>
                    <p className="text-sm text-slate-500">{el ? 'Το συνεργείο συνδέεται με αυτά τα στοιχεία και βλέπει μόνο το chat του με τους υπευθύνους στόλου.' : 'The workshop logs in with these details and only sees its chat with fleet managers.'}</p>
                    <div className="space-y-2">
                        {accounts.map((a) => (
                            <div key={a.id} className="flex items-center justify-between border border-slate-100 rounded-sm px-3 py-2 text-sm" data-testid={`workshop-account-${a.id}`}>
                                <span>{a.name} · {a.email}</span>
                                <Button variant="ghost" size="sm" onClick={() => remove(a)} className="text-red-500" data-testid={`workshop-account-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 gap-2 border-t border-slate-100 pt-3">
                        <Input placeholder={el ? 'Όνομα' : 'Name'} value={form.name} onChange={set('name')} data-testid="workshop-account-name" />
                        <Input type="email" placeholder="Email" value={form.email} onChange={set('email')} data-testid="workshop-account-email" />
                        <Input type="password" placeholder={el ? 'Κωδικός (τουλάχιστον 6)' : 'Password (min 6)'} value={form.password} onChange={set('password')} data-testid="workshop-account-password" />
                        <Button onClick={create} disabled={saving || !form.name || !form.email || form.password.length < 6} className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="workshop-account-save">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Δημιουργία λογαριασμού' : 'Create account')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
